import type { PointArray } from '../types/contour-definition-array-type';

import { getPolygonAreaAndCentroid } from './getPolygonAreaAndCentroid';
import { getPolygonIntersection } from './getPolygonIntersection';
import { isPolygonInside } from './isPolygonInside';

/**
 * Checks whether an epicardial contour validly encloses the endocardial one.
 *
 * Returns `false` when the epi was drawn inside / cutting into the endo,
 * signalling that the epi drawing should be discarded. Specifically it fails
 * when:
 *   - the epi lies entirely inside the endo, or
 *   - clipping the endo to the epi would remove more than `1 - minKeptFraction`
 *     of the endo area.
 *
 * Both polygons must be in the same coordinate space.
 */
export function epiContainsEndo(
  epi: PointArray,
  endo: PointArray,
  minKeptFraction = 0.9,
): boolean {
  if (epi.length < 4 || endo.length < 4) return true;

  // Epi drawn fully inside the endo → invalid.
  if (isPolygonInside(epi, endo)) return false;

  const endoArea = Math.abs(getPolygonAreaAndCentroid(endo).signedArea);
  if (endoArea <= 0) return true;

  const overlap = getPolygonIntersection(endo, epi);
  if (overlap.length < 4) return false;

  const keptArea = Math.abs(getPolygonAreaAndCentroid(overlap).signedArea);

  return keptArea / endoArea >= minKeptFraction;
}
